import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { orpc } from '@/api/rpc/client';
import { toast } from '@/components/ui/toast';
import { dismissPriceBreakNudge } from './dismissedPriceBreaks';
import { isPriceBreakOfferChanged } from './priceBreakOfferChanged';
import { withApprovalState } from './setApprovalStateCache';
import type {
  ListSupplierSummariesInput,
  ListSupplierSummariesResponse,
  SupplierSummaryRow,
  GetSupplierDetailInput,
  GetSupplierDetailResponse,
  SupplierSuggestionRow,
  GetSuggestionDetailInput,
  GetSuggestionDetailResponse,
  SuggestionOption,
  ListTransferSummariesInput,
  ListTransferSummariesResponse,
  TransferSummaryRow,
  ListNoSourceSuggestionsResponse,
  NoSourceRow,
  ListVendorlessTransferSummariesResponse,
  SourceTransferSummaryRow,
  GetSourceTransferAssignmentsInput,
  GetSourceTransferAssignmentsResponse,
  ListSupplierSummariesWithCartsResponse,
  ListBuyingOrdersInput,
  ListBuyerCodesInput,
  ListBuyerCodesResponse,
  GetBoardFreshnessInput,
  GetBoardFreshnessResponse,
  ListBuyingOrdersResponse,
  BuyingOrderRow,
  SupplierWithCartsSummaryRow,
  VendorlessTransfersRow,
  CartSummary,
  GetCartLinesResponse,
  CartLineRow,
  GetSupplierAssignmentsInput,
  GetSupplierAssignmentsResponse,
  AssignmentLine,
  CartAssignment,
  ConfirmCartsResponse,
  ListReplenishmentPurchaseOrdersInput,
  ListReplenishmentPurchaseOrdersResponse,
  ListReplenishmentTransferOrdersInput,
  ListReplenishmentTransferOrdersResponse,
  DraftPurchaseOrderWorkflowState,
  DraftPurchaseOrderDetail,
  ReplenishmentPurchaseOrderRow,
  ReplenishmentTransferOrderRow,
  GetReplenishmentTransferOrderLinesInput,
  GetReplenishmentTransferOrderLinesResponse,
  GetNeedInsightsInput,
  GetNeedInsightsResponse,
  GetLineStockPositionInput,
  GetLineStockPositionResponse,
  LineStockLocation,
  GetLineSupplyDemandInput,
  GetLineSupplyDemandResponse,
  GetProductInsightsInput,
  GetProductInsightsResponse,
  NeedInventoryRow,
  NeedSalesPoint,
  GetProductSalesHistoryInput,
  GetProductSalesHistoryResponse,
  GetProductMovementHistoryInput,
  GetProductMovementHistoryResponse,
  MovementHistoryPoint,
  MovementByLocationRow,
  GetProductOnHandHistoryInput,
  GetProductOnHandHistoryResponse,
  OnHandHistoryPoint,
  ComposePlanInput,
  ComposePlanLeg,
  ComposePlanResponse,
  GetAdHocTransferPlanningInput,
  GetAdHocTransferPlanningResponse,
  CartForwardBuySummary,
  OptionForwardBuyDetail,
  FulfillmentCandidatesInput,
} from '@erp/rpc';
import type { TroubleData } from '@erp/shared-types';
import { hasActiveTroubleValue } from '@/components/common/troubleEntries';

const DISABLED_DRAFT_ID = '00000000-0000-4000-8000-000000000000';

/**
 * Whether this line's commitment is the BUYER's to set, so an apply-option
 * result should be written into the cached row.
 *
 * Engine-committed lines are excluded — flipping those would clobber the
 * server's own decision. A credit-held SO line IS buyer-committed despite its
 * `sales_order` trigger, because the hold withholds the engine's commitment
 * until the buyer overrides it.
 */
function isBuyerCommitted(line: {
  trigger: string;
  triggerSalesOrderTroubleData?: TroubleData;
}): boolean {
  return (
    line.trigger === 'deficit' ||
    hasActiveTroubleValue(line.triggerSalesOrderTroubleData, 'CREDIT_HOLD')
  );
}

export type {
  ConfirmCartsResponse,
  ListReplenishmentPurchaseOrdersResponse,
  ListReplenishmentTransferOrdersResponse,
  ReplenishmentPurchaseOrderRow,
  ReplenishmentTransferOrderRow,
  GetNeedInsightsResponse,
  GetLineStockPositionResponse,
  LineStockLocation,
  GetLineSupplyDemandResponse,
  GetProductInsightsResponse,
  NeedInventoryRow,
  NeedSalesPoint,
  GetProductSalesHistoryResponse,
  GetProductMovementHistoryResponse,
  MovementHistoryPoint,
  MovementByLocationRow,
  GetProductOnHandHistoryResponse,
  OnHandHistoryPoint,
  ComposePlanInput,
  ComposePlanLeg,
  ComposePlanResponse,
};

export type {
  ListSupplierSummariesResponse,
  SupplierSummaryRow,
  GetSupplierDetailResponse,
  SupplierSuggestionRow,
  GetSuggestionDetailResponse,
  SuggestionOption,
  ListTransferSummariesResponse,
  TransferSummaryRow,
  ListNoSourceSuggestionsResponse,
  NoSourceRow,
  ListVendorlessTransferSummariesResponse,
  SourceTransferSummaryRow,
  GetSourceTransferAssignmentsResponse,
  ListSupplierSummariesWithCartsResponse,
  ListBuyingOrdersResponse,
  BuyingOrderRow,
  SupplierWithCartsSummaryRow,
  VendorlessTransfersRow,
  CartSummary,
  GetCartLinesResponse,
  CartLineRow,
  GetSupplierAssignmentsResponse,
  AssignmentLine,
  CartAssignment,
  CartForwardBuySummary,
  OptionForwardBuyDetail,
};

export function useSupplierSummaries(
  input: ListSupplierSummariesInput,
  enabled = true,
) {
  return useQuery<ListSupplierSummariesResponse>({
    ...orpc.procurement.replenishmentSuggestions.listSupplierSummaries.queryOptions(
      { input },
    ),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useSupplierDetail(
  input: GetSupplierDetailInput,
  enabled = true,
) {
  return useQuery<GetSupplierDetailResponse>({
    ...orpc.procurement.replenishmentSuggestions.getSupplierDetail.queryOptions(
      {
        input,
      },
    ),
    enabled: enabled && !!input.supplierId,
    placeholderData: (prev) => prev,
  });
}

export function useSuggestionDetail(
  input: GetSuggestionDetailInput,
  enabled = true,
) {
  return useQuery<GetSuggestionDetailResponse>({
    ...orpc.procurement.replenishmentSuggestions.getSuggestionDetail.queryOptions(
      { input },
    ),
    enabled: enabled && !!input.suggestionId,
  });
}

export function useBuyingOrders(input: ListBuyingOrdersInput, enabled = true) {
  return useQuery<ListBuyingOrdersResponse>({
    ...orpc.procurement.replenishmentSuggestions.listBuyingOrders.queryOptions({
      input,
    }),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useBuyerCodes(input: ListBuyerCodesInput, enabled = true) {
  return useQuery<ListBuyerCodesResponse>({
    ...orpc.procurement.replenishmentSuggestions.listBuyerCodes.queryOptions({
      input,
    }),
    enabled,
    placeholderData: (prev) => prev,
  });
}

/**
 * When the board's run and stock data were last refreshed (GEN-3149). Polls so
 * a board left open overnight picks up the morning's refresh.
 */
export function useBoardFreshness(
  input: GetBoardFreshnessInput,
  enabled = true,
) {
  return useQuery<GetBoardFreshnessResponse>({
    ...orpc.procurement.replenishmentSuggestions.getBoardFreshness.queryOptions(
      {
        input,
      },
    ),
    // No placeholder: the previous selection's times must not show for the
    // newly selected divisions while they load.
    enabled,
    refetchInterval: 5 * 60_000,
  });
}

/** The forecast grid's filter options, under the buying board's permission. */
export function useBuyingFilterOptions() {
  return useQuery({
    ...orpc.procurement.replenishmentSuggestions.buyingFilterOptions.queryOptions(
      { input: {} },
    ),
    staleTime: 5 * 60_000,
  });
}

export function useSupplierAssignments(
  input: GetSupplierAssignmentsInput,
  enabled = true,
  // For a reader of lines another view already loaded and keeps fresh: use the
  // cached response as is. Fetches only when nothing is cached yet.
  readCached = false,
) {
  return useQuery<GetSupplierAssignmentsResponse>({
    ...orpc.procurement.replenishmentSuggestions.getSupplierAssignments.queryOptions(
      { input },
    ),
    enabled: enabled && !!input.supplierId,
    ...(readCached ? { staleTime: Infinity } : {}),
  });
}

/**
 * Unsent draft POs (awaiting approval, or approved and not yet sent) for one
 * vendor × ship-to. Release offers to add lines to one of them.
 */
export function useOpenDraftPurchaseOrders(
  input: { supplierId: string; shipToWarehouseId: string },
  enabled = true,
) {
  return useQuery(
    orpc.procurement.replenishmentSuggestions.listOpenDraftPurchaseOrders.queryOptions(
      { input, enabled },
    ),
  );
}

export function useApplyOption() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.applyOption.mutationOptions({
      onMutate: ({ suggestionId, optionId }) => {
        // Optimistic updates so the slidein + cart toggle feel instant. We
        // skip the full-tree invalidation on success (see below); aggregate
        // views reconcile on their next natural fetch.
        //
        // Two caches need patching:
        //   1. getSupplierAssignments  → flips `recommendedAction` on the
        //      cart-view line so the cart toggle's filled/outline state
        //      updates immediately. Only lines the ENGINE did not commit flip
        //      via apply-option; guard against clobbering the ones it did.
        //
        //      That used to mean "deficits only", because every SO line was
        //      engine-committed. A credit-held SO line is not: it starts
        //      uncommitted and the buyer commits it by hand, so it flips too.
        //      Missing that left the cached line reading uncommitted after the
        //      SERVER had committed it, and `excludeSuggestionIds` (built from
        //      `isLineCommitted`) then omitted it on deselect — so Confirm
        //      reloaded the server row and ordered a line the buyer had
        //      unchecked.
        //   2. getSuggestionDetail     → flips `chosenOptionId` so the
        //      slidein's middle panel highlights the newly-picked option
        //      and PlanBuilder's `seedFromEffective` reseeds from it.
        const assignmentsKey =
          orpc.procurement.replenishmentSuggestions.getSupplierAssignments.key();
        queryClient.setQueriesData<GetSupplierAssignmentsResponse>(
          { queryKey: assignmentsKey },
          (old) => {
            if (!old) return old;
            const idx = old.lines.findIndex(
              (l) => l.suggestionId === suggestionId,
            );
            if (idx < 0) return old;
            const line = old.lines[idx];
            if (!line || !isBuyerCommitted(line)) return old;
            const newAction =
              optionId != null ? (line.topOption?.action ?? null) : null;
            const lines = old.lines.slice();
            lines[idx] = { ...line, recommendedAction: newAction };
            return { ...old, lines };
          },
        );

        // 1b. getSourceTransferAssignments → same flip for the transfer board,
        //     which reads a different query than the supplier board. Without
        //     this, toggling a vendorless-transfer line looked like a no-op
        //     until a manual reload.
        const transferKey =
          orpc.procurement.replenishmentSuggestions.getSourceTransferAssignments.key();
        queryClient.setQueriesData<GetSourceTransferAssignmentsResponse>(
          { queryKey: transferKey },
          (old) => {
            if (!old) return old;
            const idx = old.lines.findIndex(
              (l) => l.suggestionId === suggestionId,
            );
            if (idx < 0) return old;
            const line = old.lines[idx];
            if (!line || !isBuyerCommitted(line)) return old;
            const newAction =
              optionId != null ? (line.topOption?.action ?? null) : null;
            const lines = old.lines.slice();
            lines[idx] = { ...line, recommendedAction: newAction };
            return { ...old, lines };
          },
        );

        const detailKey =
          orpc.procurement.replenishmentSuggestions.getSuggestionDetail.key();
        queryClient.setQueriesData<GetSuggestionDetailResponse>(
          { queryKey: detailKey },
          (old) => {
            if (!old || old.suggestionId !== suggestionId) return old;
            return { ...old, chosenOptionId: optionId };
          },
        );
      },
      onError: () => {
        // Mutation failed — drop the optimistic patches by invalidating the
        // affected queries. Buyer sees the rows snap back to their real
        // state.
        void queryClient.invalidateQueries({
          queryKey:
            orpc.procurement.replenishmentSuggestions.getSupplierAssignments.key(),
        });
        void queryClient.invalidateQueries({
          queryKey:
            orpc.procurement.replenishmentSuggestions.getSuggestionDetail.key(),
        });
        void queryClient.invalidateQueries({
          queryKey:
            orpc.procurement.replenishmentSuggestions.getSourceTransferAssignments.key(),
        });
      },
      // The line caches already reflect the change. Other aggregate views
      // refresh on their next natural fetch, but the Orders rows carry the
      // order readiness the open cart's header shows (GEN-3234): a commit
      // moves its total and line counts, so those rows refetch now.
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey:
            orpc.procurement.replenishmentSuggestions.listBuyingOrders.key(),
        });
      },
    }),
  );
}

/**
 * Set the buyer's approval state (approved, unapproved, Trouble) on
 * suggestions. Takes an array of ids so bulk actions share the one call. The
 * supplier board's cache is patched optimistically; an error restores the
 * previous cache, and the board is reconciled once the last in-flight change
 * settles.
 */
export function useSetSuggestionApprovalState() {
  const queryClient = useQueryClient();
  // Both boards' line queries: the buyer board's and the transfer board's.
  const lineKeys = [
    orpc.procurement.replenishmentSuggestions.getSupplierAssignments.key(),
    orpc.procurement.replenishmentSuggestions.getSourceTransferAssignments.key(),
  ];
  const mutationKey =
    orpc.procurement.replenishmentSuggestions.setSuggestionApprovalState.mutationKey();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.setSuggestionApprovalState.mutationOptions(
      {
        mutationKey,
        onMutate: async ({ suggestionIds, state }) => {
          await Promise.all(
            lineKeys.map((queryKey) => queryClient.cancelQueries({ queryKey })),
          );
          const previous = lineKeys.flatMap((queryKey) =>
            queryClient.getQueriesData<
              | GetSupplierAssignmentsResponse
              | GetSourceTransferAssignmentsResponse
            >({ queryKey }),
          );
          const ids = new Set(suggestionIds);
          for (const queryKey of lineKeys) {
            queryClient.setQueriesData<
              | GetSupplierAssignmentsResponse
              | GetSourceTransferAssignmentsResponse
            >({ queryKey }, (old) => {
              if (!old) return old;
              const lines = withApprovalState(old.lines, ids, state);
              return lines === old.lines ? old : { ...old, lines };
            });
          }
          return { previous };
        },
        onError: (_error, _input, context) => {
          for (const [key, data] of context?.previous ?? []) {
            queryClient.setQueryData(key, data);
          }
        },
        onSettled: () => {
          // Rapid toggles each patch the cache; refetching under a later one
          // would flash its optimistic state away, so only the last reconciles.
          if (queryClient.isMutating({ mutationKey }) > 1) return;
          for (const queryKey of lineKeys) {
            void queryClient.invalidateQueries({ queryKey });
          }
          // Readiness is judged on approved lines (GEN-3234), so the Orders
          // rows (and the open cart's header) move with every approval.
          void queryClient.invalidateQueries({
            queryKey:
              orpc.procurement.replenishmentSuggestions.listBuyingOrders.key(),
          });
        },
      },
    ),
  );
}

/**
 * True while any approval-state write is in flight. Release reads approvals
 * from the server, so it waits for these to land rather than release a line
 * the board already shows approved but the server does not yet.
 */
export function useIsSavingApprovals(): boolean {
  return (
    useIsMutating({
      mutationKey:
        orpc.procurement.replenishmentSuggestions.setSuggestionApprovalState.mutationKey(),
    }) > 0
  );
}

// Trash (status='rejected') or restore (status='pending') a suggestion. Flips
// the row off/on the board; invalidates the tree so the board reconciles.
export function useSetSuggestionStatus() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.setSuggestionStatus.mutationOptions(
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: orpc.procurement.replenishmentSuggestions.key(),
          });
        },
      },
    ),
  );
}

/**
 * Trash or restore several lines in one call (GEN-2938): a need row acts on
 * all its open SO lines at once. Released lines are left alone server-side.
 */
export function useSetSuggestionStatuses() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.setSuggestionStatuses.mutationOptions(
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: orpc.procurement.replenishmentSuggestions.key(),
          });
        },
      },
    ),
  );
}

export function useOverrideOption() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.override.mutationOptions({
      onSuccess: () => {
        // A user-authored option changes the suggestion's options + (by default)
        // its chosen option, which can move cart placement and totals.
        void queryClient.invalidateQueries({
          queryKey: orpc.procurement.replenishmentSuggestions.key(),
        });
      },
    }),
  );
}

/**
 * Add a stock line for a need's overflow (GEN-2938). A new line moves cart
 * totals like an override, so the namespace is invalidated the same way.
 */
export function useCreateOverflowLine() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.createOverflowLine.mutationOptions(
      {
        // Returned, so the mutation stays pending until the board refetches:
        // until then a second edit would not see the new line and add another.
        onSuccess: () =>
          queryClient.invalidateQueries({
            queryKey: orpc.procurement.replenishmentSuggestions.key(),
          }),
      },
    ),
  );
}

/**
 * Save a buyer's terms on buy lines (GEN-2943). A revised cost reprices the
 * line's plan, which moves cart totals and minimums, so the whole namespace is
 * invalidated, as for an override.
 */
export function useSetLineBuyTerms() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.setLineBuyTerms.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.procurement.replenishmentSuggestions.key(),
        });
      },
    }),
  );
}

/**
 * Set a revised cost per buy line (GEN-3141): a percentage adjust, one set
 * cost, or a reset across several lines. Invalidates like a terms save.
 */
export function useSetRevisedUnitCosts() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.setRevisedUnitCosts.mutationOptions(
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: orpc.procurement.replenishmentSuggestions.key(),
          });
        },
      },
    ),
  );
}

/**
 * Remove forward buying from one vendor cart: its uplifted lines go back to
 * their baseline quantities. That moves the cart's quantities, totals,
 * threshold gap and forward-buy summary, all of which the board reads live, so
 * the whole namespace is invalidated, as for any other override.
 */
export function useRemoveForwardBuy() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.removeForwardBuy.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.procurement.replenishmentSuggestions.key(),
        });
      },
    }),
  );
}

export function useFulfillmentCandidates(
  input: FulfillmentCandidatesInput,
  enabled = true,
) {
  return useQuery({
    ...orpc.procurement.replenishmentSuggestions.getFulfillmentCandidates.queryOptions(
      { input },
    ),
    enabled: enabled && !!input.destinationWarehouseId && !!input.productV3Key,
    staleTime: 30_000,
  });
}

export function useAdHocTransferPlanning(
  input: GetAdHocTransferPlanningInput,
  enabled = true,
) {
  return useQuery<GetAdHocTransferPlanningResponse>({
    ...orpc.procurement.replenishmentSuggestions.getAdHocTransferPlanning.queryOptions(
      { input },
    ),
    enabled:
      enabled &&
      input.sourceWarehouseId !== '' &&
      input.destinationWarehouseId !== '' &&
      input.productV3Keys.length > 0,
  });
}

export function useReplenishmentForSalesOrder(
  salesOrderId: string | undefined,
  enabled = true,
) {
  return useQuery({
    ...orpc.procurement.replenishmentSuggestions.getForSalesOrder.queryOptions({
      input: { salesOrderId: salesOrderId ?? '' },
    }),
    enabled: enabled && !!salesOrderId,
    staleTime: 30_000,
  });
}

export function useRefreshReplenishment() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.refresh.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.procurement.replenishmentSuggestions.key(),
        });
      },
    }),
  );
}

export function useTransferSummaries(
  input: ListTransferSummariesInput,
  enabled = true,
) {
  return useQuery<ListTransferSummariesResponse>({
    ...orpc.procurement.replenishmentSuggestions.listTransferSummaries.queryOptions(
      { input },
    ),
    enabled,
    placeholderData: (prev) => prev,
  });
}

// Vendorless-transfer strip cards, grouped by source warehouse.
// All vendorless-transfer lines + destination carts for one source warehouse.
export function useSourceTransferAssignments(
  input: GetSourceTransferAssignmentsInput,
  enabled = true,
  // As useSupplierAssignments: read another view's cached lines as they are.
  readCached = false,
) {
  return useQuery<GetSourceTransferAssignmentsResponse>({
    ...orpc.procurement.replenishmentSuggestions.getSourceTransferAssignments.queryOptions(
      { input },
    ),
    enabled: enabled && !!input.sourceWarehouseId,
    ...(readCached ? { staleTime: Infinity } : {}),
  });
}

// Confirm one or more vendor carts → create DRAFT POs + TOs. Pass
// acknowledgeBelowMov to also create carts below the vendor's hard MOV.
export function useConfirmCarts() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.confirmCarts.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.procurement.replenishmentSuggestions.key(),
        });
      },
    }),
  );
}

// Confirm vendorless-transfer carts (source → dest) → create DRAFT TOs.
export function useConfirmTransfers() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.confirmTransfers.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.procurement.replenishmentSuggestions.key(),
        });
      },
    }),
  );
}

export function useCreateAdHocPurchaseOrder() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.createAdHocPurchaseOrder.mutationOptions(
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: orpc.procurement.replenishmentSuggestions.key(),
          });
          void queryClient.invalidateQueries({
            queryKey: orpc.accountsPayable.purchaseOrders.list.key(),
          });
        },
      },
    ),
  );
}

export function useRetryAdHocPurchaseOrderApprovalDispatch() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.retryAdHocPurchaseOrderApprovalDispatch.mutationOptions(
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: orpc.procurement.replenishmentSuggestions.key(),
          });
        },
      },
    ),
  );
}

export function useCreateAdHocTransferOrder() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.createAdHocTransferOrder.mutationOptions(
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: orpc.procurement.replenishmentSuggestions.key(),
          });
        },
      },
    ),
  );
}

export function useSaveAdHocTransferWorksheet() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.saveAdHocTransferWorksheet.mutationOptions(
      {
        onSuccess: (worksheet) => {
          queryClient.setQueryData(
            orpc.procurement.replenishmentSuggestions.getAdHocTransferWorksheet.queryKey(
              { input: { draftId: worksheet.draftId } },
            ),
            worksheet,
          );
          void queryClient.invalidateQueries({
            queryKey:
              orpc.procurement.replenishmentSuggestions.listReplenishmentTransferOrders.key(),
          });
        },
      },
    ),
  );
}

export function useAdHocTransferWorksheet(
  draftId: string | undefined,
  enabled = true,
) {
  return useQuery({
    ...orpc.procurement.replenishmentSuggestions.getAdHocTransferWorksheet.queryOptions(
      { input: { draftId: draftId ?? DISABLED_DRAFT_ID } },
    ),
    enabled: enabled && draftId !== undefined,
  });
}

// Replenishment-created POs for the procurement orders view (paginated).
export function useReplenishmentPurchaseOrders(
  input: ListReplenishmentPurchaseOrdersInput,
  enabled = true,
) {
  return useQuery<ListReplenishmentPurchaseOrdersResponse>({
    ...orpc.procurement.replenishmentSuggestions.listReplenishmentPurchaseOrders.queryOptions(
      { input },
    ),
    enabled,
    placeholderData: (prev) => prev,
  });
}

// Replenishment-created TOs for the procurement orders view (paginated).
export function useReplenishmentTransferOrders(
  input: ListReplenishmentTransferOrdersInput,
  enabled = true,
) {
  return useQuery<ListReplenishmentTransferOrdersResponse>({
    ...orpc.procurement.replenishmentSuggestions.listReplenishmentTransferOrders.queryOptions(
      { input },
    ),
    enabled,
    placeholderData: (prev) => prev,
    // The list is expensive (joins + per-page line counts); don't refetch it
    // on every window focus / remount.
    staleTime: 30_000,
  });
}

// Approval-progress pipeline for a single held PO (drill-in dialog). Fetched
// on demand — pass enabled=false until the dialog opens.
export function useDraftPurchaseOrderWorkflowState(
  draftPurchaseOrderId: string,
  enabled = true,
) {
  return useQuery<DraftPurchaseOrderWorkflowState>({
    ...orpc.procurement.replenishmentSuggestions.getDraftPurchaseOrderWorkflowState.queryOptions(
      { input: { draftPurchaseOrderId } },
    ),
    enabled,
  });
}

// Header + lines of a single draft PO — the shared read model behind the
// inbox approval pane and the orders-board drill-in. Fetched on demand.
export function useDraftPurchaseOrderDetail(
  draftPurchaseOrderId: string,
  enabled = true,
) {
  return useQuery<DraftPurchaseOrderDetail>({
    ...orpc.procurement.replenishmentSuggestions.getDraftPurchaseOrderDetail.queryOptions(
      { input: { draftPurchaseOrderId } },
    ),
    enabled: enabled && !!draftPurchaseOrderId,
  });
}

// Retry an errored replenishment draft order (flip errored → pending).
export function useRetryReplenishmentOrder() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.retryReplenishmentOrder.mutationOptions(
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: orpc.procurement.replenishmentSuggestions.key(),
          });
        },
      },
    ),
  );
}

// Recall a pre-ack replenishment draft order (soft-delete + revert suggestions).
export function useRecallReplenishmentOrder() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.recallReplenishmentOrder.mutationOptions(
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: orpc.procurement.replenishmentSuggestions.key(),
          });
        },
      },
    ),
  );
}

// Cancel an auto-staged transfer; `block` also stops future auto-transfers of
// the order's products to that branch.
export function useCancelStagedTransfer() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.cancelStagedTransfer.mutationOptions(
      {
        onSuccess: () => {
          // Scope to the transfer portal's queries — invalidating the whole
          // replenishment tree also busts the board's caches and the insights
          // panels, none of which are affected by a cancel. (Board queries
          // refetch on their next mount regardless.)
          void queryClient.invalidateQueries({
            queryKey:
              orpc.procurement.replenishmentSuggestions.listReplenishmentTransferOrders.key(),
          });
          void queryClient.invalidateQueries({
            queryKey:
              orpc.procurement.replenishmentSuggestions.getReplenishmentTransferOrderLines.key(),
          });
        },
      },
    ),
  );
}

// Line items of a single (draft) transfer order for the transfers-portal
// row drill-in.
export function useReplenishmentTransferOrderLines(
  input: GetReplenishmentTransferOrderLinesInput,
  enabled = true,
) {
  return useQuery<GetReplenishmentTransferOrderLinesResponse>({
    ...orpc.procurement.replenishmentSuggestions.getReplenishmentTransferOrderLines.queryOptions(
      { input },
    ),
    enabled: enabled && !!input.id,
    // Clicking back and forth between routes should hit the cache, not refetch
    // a (potentially large) line list every time.
    staleTime: 30_000,
  });
}

// Per-need decision-support data for the composite-builder modal.
export function useNeedInsights(input: GetNeedInsightsInput, enabled = true) {
  return useQuery<GetNeedInsightsResponse>({
    ...orpc.procurement.replenishmentSuggestions.getNeedInsights.queryOptions({
      input,
    }),
    enabled: enabled && !!input.suggestionId,
    staleTime: 60_000,
  });
}

// A buyer-board line's stock position at every location (GEN-2944): the Line
// section's live position and its Stock Elsewhere pop-out.
export function useLineStockPosition(
  input: GetLineStockPositionInput,
  enabled = true,
) {
  return useQuery<GetLineStockPositionResponse>({
    ...orpc.procurement.replenishmentSuggestions.getLineStockPosition.queryOptions(
      { input },
    ),
    enabled: enabled && !!input.suggestionId,
    staleTime: 60_000,
  });
}

// The order lines behind a board line's on order and open demand (GEN-3285):
// the Line footer's Supply tab, read only while that tab is open.
export function useLineSupplyDemand(
  input: GetLineSupplyDemandInput,
  enabled = true,
) {
  return useQuery<GetLineSupplyDemandResponse>({
    ...orpc.procurement.replenishmentSuggestions.getLineSupplyDemand.queryOptions(
      { input },
    ),
    enabled: enabled && !!input.suggestionId,
    staleTime: 60_000,
  });
}

// Same buyer's-menu insights as useNeedInsights, but keyed by a (product,
// variant, destination-warehouse) tuple instead of a suggestion — for pages
// that show line items without a replenishment suggestion (e.g. outgoing
// transfers).
export function useProductInsights(
  input: GetProductInsightsInput,
  enabled = true,
) {
  return useQuery<GetProductInsightsResponse>({
    ...orpc.procurement.replenishmentSuggestions.getProductInsights.queryOptions(
      { input },
    ),
    enabled: enabled && !!input.productV3Key && !!input.destinationWarehouseId,
    staleTime: 60_000,
  });
}

// Standalone sales history for the item-detail chart (no suggestion context).
export function useProductSalesHistory(
  input: GetProductSalesHistoryInput,
  enabled = true,
) {
  return useQuery<GetProductSalesHistoryResponse>({
    ...orpc.procurement.replenishmentSuggestions.getProductSalesHistory.queryOptions(
      { input },
    ),
    enabled: enabled && !!input.productV3Key,
    staleTime: 60_000,
  });
}

// 4-series movement history (sales / transfers-out / buys / transfers-in)
// for the product-detail outbound-vs-inbound chart.
export function useProductMovementHistory(
  input: GetProductMovementHistoryInput,
  enabled = true,
) {
  return useQuery<GetProductMovementHistoryResponse>({
    ...orpc.procurement.replenishmentSuggestions.getProductMovementHistory.queryOptions(
      { input },
    ),
    enabled: enabled && !!input.productV3Key,
    staleTime: 60_000,
  });
}

// End-of-month on-hand history. Used as the on-hand overlay line on the
// sales + movement charts.
export function useProductOnHandHistory(
  input: GetProductOnHandHistoryInput,
  enabled = true,
) {
  return useQuery<GetProductOnHandHistoryResponse>({
    ...orpc.procurement.replenishmentSuggestions.getProductOnHandHistory.queryOptions(
      { input },
    ),
    enabled: enabled && !!input.productV3Key,
    staleTime: 60_000,
  });
}

// Compose a multi-leg buyer plan. Writes one source:'user' option with N legs
// and (by default) makes it the chosen option for the suggestion.
export function useComposePlan() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.replenishmentSuggestions.composePlan.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.procurement.replenishmentSuggestions.key(),
        });
      },
    }),
  );
}

// Take a price-break nudge. Sends only which line and which option; the server
// decides the quantity (the next break rounded to the vendor's MOQ and order
// multiple) and the price, and writes a chosen source:'user' option exactly as
// the override path does, so it invalidates the same queries.
//
// When the vendor row the nudge was priced on has changed or gone, the server
// refuses with PRICE_BREAK_OFFER_CHANGED. That is not a failed save: the nudge
// frozen on that option is stale and would be refused again, so the board
// dismisses it (see dismissedPriceBreaks), tells the buyer, and refetches
// exactly as a success would. Callers skip their own error toast for it.
export function useApplyPriceBreak() {
  const gt = useGT();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: orpc.procurement.replenishmentSuggestions.key(),
    });
  };
  return useMutation(
    orpc.procurement.replenishmentSuggestions.applyPriceBreak.mutationOptions({
      onSuccess: refresh,
      onError: (error, input) => {
        if (!isPriceBreakOfferChanged(error)) return;
        dismissPriceBreakNudge(input.optionId);
        toast.warning(
          gt(
            "The vendor's offer changed, so this price break was not applied. It is dismissed until the next replenishment run.",
            {
              context:
                'replenishment price-break nudge: the vendor price the nudge was based on changed, so nothing was ordered and the nudge is hidden until the replenishment engine runs again',
            },
          ),
        );
        refresh();
      },
    }),
  );
}
